import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import React, { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import SwitchInput, { SwitchInputProps } from './SwitchInput';

export const SubTogglesKeys = Object.freeze({
  PRIMARY_TOGGLE: 'enabled',
  SECONDARY_TOGGLE: 'ai_enabled',
});

type Props = SwitchInputProps & {
  secondaryToggle?: SwitchInputProps & {
    hidden: boolean;
    defaultSecondaryToggleValue: boolean;
    label?: string | ReactNode;
    helperText?: string | ReactNode;
  };
};

const SwitchInputWithSubToggle: FC<Props> = ({
  onChange,
  secondaryToggle,
  ...props
}) => {
  const [state, setState] = useState<{
    [SubTogglesKeys.PRIMARY_TOGGLE]: boolean | undefined;
    [SubTogglesKeys.SECONDARY_TOGGLE]: boolean | undefined;
  }>({
    [SubTogglesKeys.PRIMARY_TOGGLE]: undefined,
    [SubTogglesKeys.SECONDARY_TOGGLE]: undefined,
  });

  useEffect(() => {
    setState(props.value);
  }, [props.value]);

  const onChangePrimaryToggle = useCallback(
    (value) => {
      const newState = { ...state, [SubTogglesKeys.PRIMARY_TOGGLE]: value };
      setState(newState);
      onChange && onChange(newState);
    },
    [state, onChange]
  );

  const onChangeSecondaryToggle = useCallback(
    (value) => {
      const newState = { ...state, [SubTogglesKeys.SECONDARY_TOGGLE]: value };
      setState(newState);
      onChange && onChange(newState);
    },
    [state, onChange]
  );

  return (
    <div id={props.name}>
      <Row>
        <Col className="pe-1">
          <SwitchInput
            {...props}
            onChange={onChangePrimaryToggle}
            value={state?.[SubTogglesKeys.PRIMARY_TOGGLE]}
          />
        </Col>
        {secondaryToggle && !secondaryToggle.hidden && (
          <Col className="ms-auto d-flex align-items-center gap-2">
            <SwitchInput
              value={
                state?.[SubTogglesKeys.SECONDARY_TOGGLE] !== undefined
                  ? state?.[SubTogglesKeys.SECONDARY_TOGGLE]
                  : secondaryToggle.defaultSecondaryToggleValue
              }
              onChange={onChangeSecondaryToggle}
              {...secondaryToggle}
            />
            <span className={secondaryToggle.disabled ? 'text-muted' : ''}>
              {secondaryToggle.label}
              {secondaryToggle.helperText && (
                <>
                  <i
                    id={`helper-hover-${secondaryToggle.name}`}
                    className={`ms-2 fe fe-help-circle small ${
                      secondaryToggle.disabled ? 'text-muted' : 'text-primary'
                    }`}
                  />
                  <UncontrolledPopover
                    placement="top"
                    trigger="hover"
                    target={`helper-hover-${secondaryToggle.name}`}
                  >
                    {secondaryToggle.helperText}
                  </UncontrolledPopover>
                </>
              )}
            </span>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default React.memo(SwitchInputWithSubToggle);
